import React from 'react';
import {
  Box,
  Typography
} from '@mui/material';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import ResponsiveChart from './ResponsiveChart';

const CallTrendsChart = ({ data }) => {
  // Handle case where data might be the entire response object
  const processedData = Array.isArray(data) ? data : (data?.data || []);
  
  if (!processedData || processedData.length === 0) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height={300}
        flexDirection="column"
      >
        <Typography variant="body2" color="text.secondary" gutterBottom>
          No data available
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Call trends will appear here once you have call data
        </Typography>
      </Box>
    );
  }

  // Format data for chart
  const chartData = processedData.map(item => ({
    date: new Date(item.date).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    }),
    calls: item.totalCalls || 0,
    completed: item.completedCalls || 0,
    failed: item.failedCalls || 0,
    duration: Math.round((item.totalDuration || 0) / 60), // Convert to minutes
    successRate: item.successRate || 0
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            boxShadow: 2
          }}
        >
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography 
              key={index} 
              variant="body2" 
              sx={{ color: entry.color }}
            >
              {entry.name}: {entry.value}
              {entry.name === 'Duration' ? ' min' : 
               entry.name === 'Success Rate' ? '%' : ''}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <ResponsiveChart height={350} mobileHeight={250}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 12 }}
            interval="preserveStartEnd"
          />
          <YAxis 
            yAxisId="left"
            tick={{ fontSize: 12 }}
            label={{ 
              value: 'Calls', 
              angle: -90, 
              position: 'insideLeft',
              style: { textAnchor: 'middle' }
            }}
          />
          <YAxis 
            yAxisId="right" 
            orientation="right"
            tick={{ fontSize: 12 }}
            label={{ 
              value: 'Success Rate (%)', 
              angle: 90, 
              position: 'insideRight',
              style: { textAnchor: 'middle' }
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          
          <Area
            type="monotone"
            dataKey="calls"
            stackId="1"
            stroke="#8884d8"
            fill="#8884d8"
            fillOpacity={0.6}
            name="Total Calls"
            yAxisId="left"
          />
          
          <Area
            type="monotone"
            dataKey="completed"
            stackId="1"
            stroke="#82ca9d"
            fill="#82ca9d"
            fillOpacity={0.6}
            name="Completed Calls"
            yAxisId="left"
          />
          
          <Line
            type="monotone"
            dataKey="successRate"
            stroke="#ff7300"
            strokeWidth={2}
            dot={{ fill: '#ff7300', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6 }}
            name="Success Rate"
            yAxisId="right"
          />
        </AreaChart>
      </ResponsiveContainer>
    </ResponsiveChart>
  );
};

export default CallTrendsChart; 